import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError, api, type Uuid } from "@shared/api";
import { plural } from "@shared/lib/format";
import { ServerTile } from "@entities/server";
import { Button } from "@shared/ui/button";
import { greet } from "@features/server-rules";
import { isCall } from "@entities/channel";
import { IconVoiceChannel } from "@shared/ui/icons";

/**
 * Карточка приглашения в ленте — как в Discord: ссылка на код становится
 * сервером с числом людей и кнопкой «Вступить» (или «Открыть», если уже там).
 * Негодный или истёкший код — карточка так и говорит.
 *
 * Код в голосовой канал — приглашение в голос, как в Discord: канал и сервер,
 * а кнопка сразу входит в звонок (третий довод `onOpenServer`).
 */
export function InviteCard({
  code,
  onOpenServer,
}: {
  code: string;
  onOpenServer?: (serverId: Uuid, channelId: Uuid | null, join?: boolean) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const { data: preview, isError } = useQuery({
    queryKey: ["invite", code],
    queryFn: () => api.invites.preview(code),
    retry: false,
    staleTime: 60_000,
  });

  if (isError) {
    return (
      <div className="invite-card dead">
        <span className="small">Приглашение недействительно — код истёк или его отозвали.</span>
      </div>
    );
  }

  if (!preview) return <div className="invite-card loading" aria-hidden />;
  const voice = !!preview.channel_id && !!preview.channel_type && isCall(preview.channel_type);

  async function go() {
    if (!preview || !onOpenServer) return;
    if (preview.joined) {
      onOpenServer(preview.server_id, preview.channel_id, voice);
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      const used = await api.invites.use(code);
      if (used.joined) greet(used.server_id);
      onOpenServer(used.server_id, used.channel_id ?? null, voice);
    } catch (error) {
      setProblem(
        error instanceof ApiError && error.code === "invites_paused"
          ? "Приглашения на этот сервер приостановлены."
          : error instanceof ApiError
            ? error.message
            : "Не вышло вступить",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="invite-card">
      {preview.banner && (
        <div
          className="invite-card-banner"
          style={{
            background: `linear-gradient(160deg, ${preview.banner}, color-mix(in srgb, ${preview.banner} 55%, #000))`,
          }}
        />
      )}
      <div className="tiny invite-card-label">{voice ? "ВАС ПРИГЛАСИЛИ В ГОЛОСОВОЙ КАНАЛ" : "ВАС ПРИГЛАСИЛИ НА СЕРВЕР"}</div>
      <div className="line" style={{ gap: 12 }}>
        <ServerTile name={preview.name} id={preview.server_id} iconId={preview.icon_id} size={48} />
        <div className="grow" style={{ minWidth: 0 }}>
          {voice ? (
            <>
              <div className="invite-card-name invite-card-voice ellipsis">
                <IconVoiceChannel size={14} />
                {preview.channel_name}
              </div>
              <div className="tiny invite-card-counts ellipsis">в {preview.name}</div>
            </>
          ) : (
          <>
          <div className="invite-card-name ellipsis">{preview.name}</div>
          <div className="tiny invite-card-counts">
            <span className="invite-dot on" /> {preview.online} в сети
            <span className="invite-dot" /> {preview.members} {plural(preview.members, "участник", "участника", "участников")}
          </div>
          </>
          )}
        </div>
        {onOpenServer && (
          <Button
            type="button"
            size="sm"
            disabled={busy || (!preview.joined && !!preview.paused)}
            title={!preview.joined && preview.paused ? "Приглашения приостановлены" : undefined}
            onClick={() => void go()}
          >
            {preview.joined ? (voice ? "Присоединиться" : "Открыть") : preview.paused ? "На паузе" : "Вступить"}
          </Button>
        )}
      </div>
      {problem && <div className="tiny danger-text">{problem}</div>}
    </div>
  );
}
